<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useCharactersStore } from '@/stores/characters'
import { useConversationsStore } from '@/stores/conversations'
import { useBotsStore } from '@/features/bots/store'
import { useAuthStore } from '@/features/account/session'
import { usePersonasStore } from '@/features/personas/store'
import { useMyProfileStore } from '@/features/account/profile'
import { useLoginRedirect } from '@/composables/useLoginRedirect'
import { useIsMobile } from '@/composables/useIsMobile'
import PageContainer from '@/components/ui/PageContainer.vue'
import BaseImage from '@/components/ui/BaseImage.vue'
import CogOutlineIcon from 'vue-material-design-icons/CogOutline.vue'
import HeroConversationCard from '@/components/home/HeroConversationCard.vue'
import RecentStoryCard from '@/components/home/RecentStoryCard.vue'
import DiscoverySection from '@/components/home/DiscoverySection.vue'
import apiService from '@/api'
import { sortConversations } from '@/utils/sorting'
import type { Character, Conversation } from '@/types/domain'

type HomeConversation = Conversation & {
  character_name: string
  character_img: string | null
  character_img_focus: Character['img_focus'] | null
  persona_name: string
}

const charactersStore = useCharactersStore()
const conversationsStore = useConversationsStore()
const botsStore = useBotsStore()
const authStore = useAuthStore()
const personasStore = usePersonasStore()
const myProfileStore = useMyProfileStore()
const { goOrLogin } = useLoginRedirect()
const { isMobile } = useIsMobile()

const favoritesCharacters = ref<Character[]>([])
const favoritesLoading = ref<boolean>(false)
const conversationsLoading = ref<boolean>(false)
const RECENT_LIMIT = 8

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

const loadFavoriteCharacters = async (): Promise<void> => {
  if (!authStore.userId) {
    favoritesCharacters.value = []
    favoritesLoading.value = false
    return
  }

  favoritesLoading.value = true
  try {
    const result = await apiService.listFavoriteCharacters({ page_size: 200 }) as { characters?: Character[] } | null | undefined
    favoritesCharacters.value = Array.isArray(result?.characters) ? result.characters : []
  } catch (error: unknown) {
    void error
    favoritesCharacters.value = []
  } finally {
    favoritesLoading.value = false
  }
}

watch(() => authStore.userId, loadFavoriteCharacters)

const allUserConversations = computed<Conversation[]>(() => {
  return conversationsStore.getUserConversations(authStore.userId ?? '')
})

const recentConversationSlice = computed<Conversation[]>(() => {
  return allUserConversations.value.slice(0, RECENT_LIMIT)
})

const recentCharacterIds = computed<number[]>(() => {
  const ids = new Set<number>()
  for (const conv of recentConversationSlice.value) {
    if (conv?.character_id) ids.add(conv.character_id)
  }
  return Array.from(ids)
})

watch(
  recentCharacterIds,
  async (ids: number[]) => {
    await Promise.all(
      ids.map(async (id) => {
        if (charactersStore.getCharacterById(id)) return
        try {
          await charactersStore.fetchCharacter(id)
        } catch (error: unknown) {
          console.warn('[HomeView] Failed to fetch character', { id }, getErrorMessage(error, 'Failed to fetch character'))
        }
      }),
    )
  },
  { immediate: true },
)

const recentConversations = computed<HomeConversation[]>(() => {
  const conversations = recentConversationSlice.value
    .map((conv) => {
      const character = charactersStore.getCharacterById(conv.character_id)
      const persona = personasStore.getPersonaById(conv.persona_id)

      return {
        ...conv,
        character_name: character?.character_name || 'Unknown Character',
        character_img: character?.imgpath || null,
        character_img_focus: character?.img_focus || null,
        persona_name: persona?.persona_name || 'Current Persona',
      }
    })
    .filter((conv) => conv.character_name !== 'Unknown Character')

  return sortConversations(conversations) as HomeConversation[]
})

const heroConversation = computed<HomeConversation | null>(() => recentConversations.value[0] ?? null)
const recentStories = computed<HomeConversation[]>(() => recentConversations.value.slice(1, 7))

onMounted(async () => {
  conversationsLoading.value = true
  try {
    await Promise.all([
      conversationsStore.fetchConversations(),
      personasStore.fetchPersonas(),
      botsStore.fetchBots(),
      loadFavoriteCharacters(),
    ])
  } finally {
    conversationsLoading.value = false
  }
})

// Account controls docked into the chrome's tools region (right side), where
// most views host their action button. Same destinations the retired sidebar
// footer offered: profile (or settings when signed out) and settings.
const username = computed<string>(() => myProfileStore.username || 'User')
const avatarSrc = computed(() => myProfileStore.avatarUrl)
const avatarFocus = computed(() => myProfileStore.avatarFocus)
const profileTo = computed<string>(() => (authStore.userId ? `/user/${authStore.userId}` : '/settings'))

const goToCharacters = (): Promise<void> => goOrLogin({ name: 'characters' })
const openConversation = (conv: { id?: number | string }): Promise<void> => goOrLogin(`/conversation/${conv.id}`)
const openCharacter = (character: Character): Promise<void> => goOrLogin(`/character/${character.id}`)
</script>

<template>
  <!-- Account controls projected into the chrome's right-hand tools region. -->
  <Teleport v-if="!isMobile" to="#app-chrome-tools" defer>
    <div class="home-account">
      <RouterLink
        to="/settings"
        class="home-account__settings-btn"
        aria-label="Settings"
      >
        <CogOutlineIcon class="home-account__settings-icon" aria-hidden="true" />
      </RouterLink>
      <RouterLink
        :to="profileTo"
        class="home-account__avatar-btn"
        :aria-label="`Profile – ${username}`"
      >
        <BaseImage
          class="home-account__avatar"
          :src="avatarSrc"
          :alt="username"
          :image-focus="avatarFocus"
          fallback-size="sm"
        />
      </RouterLink>
    </div>
  </Teleport>

  <PageContainer>
    <div class="home-view">
      <!-- Hero: most recent conversation -->
      <HeroConversationCard
        :conversation="heroConversation"
        :loading="conversationsLoading"
        @continue="openConversation"
        @start-new="goToCharacters"
      />

      <!-- Recent stories row -->
      <section v-if="recentStories.length > 0" class="home-view__recent">
        <div class="home-view__section-heading-row">
          <h2 class="home-view__section-heading">Recent stories</h2>
          <button
            type="button"
            class="home-view__view-all"
            @click="goOrLogin({ name: 'conversations' })"
          >
            View all →
          </button>
        </div>
        <div class="home-view__recent-scroll">
          <RecentStoryCard
            v-for="conv in recentStories"
            :key="conv.id"
            :conversation="conv"
            @click="openConversation"
          />
        </div>
      </section>

      <!-- Discovery: favorite characters -->
      <DiscoverySection
        :characters="favoritesCharacters"
        :loading="favoritesLoading"
        @open-character="openCharacter"
        @view-all="goToCharacters"
      />
    </div>
  </PageContainer>
</template>

<style scoped>
.home-view {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 32px;
}

/* Account controls teleported into the chrome tools region. Scoped styles still
   apply across the Teleport — Vue tags the moved nodes with this component's
   scope id. */
.home-account {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.home-account__avatar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 9999px;
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color 150ms;
}

.home-account__avatar-btn:hover {
  background-color: var(--theme-surface-interactive, rgba(255, 255, 255, 0.07));
}

.home-account__avatar-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}

.home-account__avatar {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.home-account__settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0;
  height: 36px;
  padding: 0;
  overflow: hidden;
  opacity: 0;
  border-radius: 9999px;
  color: var(--theme-text-secondary);
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color 150ms, color 150ms, opacity 150ms, width 180ms ease;
}

.home-account:hover .home-account__settings-btn,
.home-account:focus-within .home-account__settings-btn {
  width: 36px;
  opacity: 1;
}

.home-account__settings-btn:hover {
  color: var(--theme-on-surface, rgba(255, 255, 255, 0.9));
  background-color: var(--theme-surface-interactive, rgba(255, 255, 255, 0.07));
}

.home-account__settings-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}

.home-account__settings-icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.home-account__settings-icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.home-view__recent {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.home-view__section-heading-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.home-view__section-heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--theme-text-secondary);
  margin: 0;
}

.home-view__view-all {
  background: transparent;
  border: none;
  color: var(--theme-primary);
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: opacity 150ms;
}

.home-view__view-all:hover {
  opacity: 0.8;
}

.home-view__recent-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
  /* subtle momentum scroll on iOS */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.home-view__recent-scroll::-webkit-scrollbar {
  display: none;
}
</style>
